import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PARENT_NAV } from "./ParentDashboard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { BookOpen, ChevronRight, ShoppingCart, Info, CheckCircle2 } from "lucide-react";

/**
 * Parent Book-Kit selector — progressive dropdowns.
 *   School → Academic Year → Class → (2nd Language) → (Subject Combination)
 * The last two fields are only shown when the underlying data has them.
 */
export default function BookKits() {
  const [schools, setSchools] = useState([]);
  const [years, setYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [langs, setLangs] = useState([]);
  const [combos, setCombos] = useState([]);

  const [school, setSchool] = useState("");
  const [year, setYear] = useState("");
  const [cls, setCls] = useState("");
  const [lang, setLang] = useState("");
  const [combo, setCombo] = useState("");

  const [kit, setKit] = useState(null);
  const [loadingKit, setLoadingKit] = useState(false);
  const cart = useCart();
  const mrpTotal = React.useMemo(
    () => (kit?.items || []).reduce(
      (s, l) => s + ((Number(l.product?.mrp) || 0) * (l.qty || 1)),
      0
    ), [kit]);
  const alreadyInCart = !!(kit && cart.items.some(
    (x) => x.type === "kit" && x.kit_id === kit.id
  ));

  // 1) On mount, load list of schools with at least one published kit.
  useEffect(() => {
    api.get("/kits/schools").then((r) => setSchools(r.data || [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  // 2) When a school is chosen, fetch academic years.
  useEffect(() => {
    setYear(""); setCls(""); setLang(""); setCombo(""); setYears([]); setClasses([]); setLangs([]); setCombos([]); setKit(null);
    if (!school) return;
    api.get("/kits/academic-years", { params: { school_id: school } }).then((r) => setYears(r.data || []));
  }, [school]);

  // 3) On year change, fetch classes.
  useEffect(() => {
    setCls(""); setLang(""); setCombo(""); setClasses([]); setLangs([]); setCombos([]); setKit(null);
    if (!school || !year) return;
    api.get("/kits/classes", { params: { school_id: school, academic_year_id: year } }).then((r) => setClasses(r.data || []));
  }, [year, school]);

  // 4) On class change, fetch languages (may be empty → skip).
  useEffect(() => {
    setLang(""); setCombo(""); setLangs([]); setCombos([]); setKit(null);
    if (!school || !year || !cls) return;
    api.get("/kits/second-languages", {
      params: { school_id: school, academic_year_id: year, class_level: cls }
    }).then((r) => setLangs(r.data || []));
  }, [cls, year, school]);

  // 5) When language is chosen (or auto-skipped), fetch combos (may be empty → skip).
  useEffect(() => {
    setCombo(""); setCombos([]); setKit(null);
    if (!school || !year || !cls) return;
    const params = { school_id: school, academic_year_id: year, class_level: cls };
    if (langs.length > 0) {
      if (!lang) return;
      params.second_language = lang;
    }
    api.get("/kits/subject-combinations", { params }).then((r) => setCombos(r.data || []));
  }, [lang, langs, cls, year, school]);

  // 6) Auto-fetch kit when all required fields are settled.
  const canFetchKit = school && year && cls
    && (langs.length === 0 || lang)
    && (combos.length === 0 || combo);
  useEffect(() => {
    setKit(null);
    if (!canFetchKit) return;
    setLoadingKit(true);
    const params = { school_id: school, academic_year_id: year, class_level: cls };
    if (langs.length > 0) params.second_language = lang;
    if (combos.length > 0) params.subject_combination = combo;
    api.get("/kits/find", { params })
      .then((r) => setKit((r.data || [])[0] || null))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingKit(false));
  }, [canFetchKit, school, year, cls, lang, combo, langs.length, combos.length]);

  const addKitToCart = () => {
    if (!kit) return;
    // Store kit_school_name / academic_year_name so the cart shows friendly labels
    const schoolObj = schools.find((s) => s.id === kit.school_id);
    const yearObj = years.find((y) => y.id === kit.academic_year_id);
    cart.addKit({
      ...kit,
      school_name: schoolObj?.name || "",
      academic_year_name: yearObj?.name || "",
    });
    toast.success(`“${kit.name}” added to cart.`);
  };

  return (
    <DashboardLayout nav={PARENT_NAV} title="Book Kits">
      <div className="max-w-5xl">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-6 text-sm text-blue-900 flex items-start gap-3">
          <Info size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">Find your child's Book Kit</div>
            <div className="text-blue-800/90 mt-1">Choose the school, year and class to view the recommended list.</div>
          </div>
        </div>

        {/* ---------- Progressive dropdowns ---------- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Field label="School" required>
            <Select value={school} onChange={setSchool} data-testid="sel-school" placeholder="Select school">
              {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
          <Field label="Academic Year" required disabled={!school}>
            <Select value={year} onChange={setYear} data-testid="sel-year" placeholder="Select year" disabled={!school}>
              {years.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
            </Select>
          </Field>
          <Field label="Class" required disabled={!year}>
            <Select value={cls} onChange={setCls} data-testid="sel-class" placeholder="Select class" disabled={!year}>
              {classes.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
          {langs.length > 0 && (
            <Field label="Second Language" required disabled={!cls}>
              <Select value={lang} onChange={setLang} data-testid="sel-lang" placeholder="Select language" disabled={!cls}>
                {langs.map((l) => <option key={l} value={l}>{l}</option>)}
              </Select>
            </Field>
          )}
          {combos.length > 0 && (
            <Field
              label={/^X[I]?$/.test(cls) ? "Stream" : "Subject Combination"}
              required
              disabled={langs.length > 0 && !lang}
            >
              <Select value={combo} onChange={setCombo} data-testid="sel-combo" placeholder="Select combination">
                {combos.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
          )}
        </div>

        {/* ---------- Result ---------- */}
        {loadingKit && <div className="mt-8 text-slate-500">Loading book kit…</div>}
        {canFetchKit && !loadingKit && !kit && (
          <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="kit-empty">
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 grid place-items-center mb-3">
              <BookOpen size={22} className="text-amber-700" />
            </div>
            <div className="font-heading font-semibold text-amber-900 text-lg">Book Kit Not Available Yet</div>
            <div className="text-sm text-amber-800/90 mt-2 max-w-md mx-auto">
              The book list for this selection has not yet been published.
              Please check again later or contact SchoolLane.
            </div>
            <div className="mt-5 flex items-center justify-center gap-2">
              <button
                data-testid="btn-change-selection"
                onClick={() => { setCls(""); setLang(""); setCombo(""); }}
                className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark"
              >
                Change selection
              </button>
              <a href="/contact" data-testid="btn-contact-support"
                 className="px-4 py-2 rounded-md border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50">
                Contact SchoolLane
              </a>
            </div>
          </div>
        )}

        {kit && (
          <div className="mt-8" data-testid="kit-detail">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-brand-blue">Recommended Kit · Demo Book List — Illustrative</div>
                  <h2 className="font-heading text-2xl text-brand-navy mt-1">{kit.name}</h2>
                  <div className="text-sm text-slate-500 mt-2 whitespace-pre-line max-w-2xl">{kit.description}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Kit Total</div>
                  <div className="font-heading text-3xl font-bold text-brand-navy" data-testid="kit-total">
                    ₹ {Number(kit.total_price || 0).toLocaleString("en-IN")}
                  </div>
                  <div className="text-xs text-slate-500">{kit.item_count} item(s)</div>
                </div>
              </div>

              <div className="mt-6 divide-y divide-slate-100">
                {(kit.items || []).map((line) => (
                  <div key={line.id} className="py-3 flex items-center gap-4" data-testid={`kit-line-${line.product_id}`}>
                    <div className="w-12 h-12 bg-slate-50 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0">
                      {line.product?.image
                        ? <img src={line.product.image} alt="" className="max-w-full max-h-full object-contain" onError={(e)=>{e.currentTarget.src="/book-cover-unavailable.svg";}}/>
                        : <BookOpen size={20} className="text-slate-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">{line.product?.name}</div>
                      <div className="text-xs text-slate-500">{line.product?.category} · Qty {line.qty}
                        {line.required && <span className="ml-2 inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12}/>Required</span>}
                      </div>
                    </div>
                    <div className="text-right text-xs text-slate-500 min-w-[110px]">
                      Individual price: <b className="text-slate-800">₹ {Number(line.unit_price || 0).toLocaleString("en-IN")}</b>
                    </div>
                  </div>
                ))}
              </div>

              {/* --------- Kit summary --------- */}
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Complete Book Kit</div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-slate-600">Total Books</span>
                  <span className="font-semibold text-slate-900" data-testid="kit-book-count">{kit.item_count}</span>
                </div>
                {mrpTotal > (kit.total_price || 0) && (
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-slate-600">MRP</span>
                    <span className="text-slate-500 line-through">₹ {Number(mrpTotal).toLocaleString("en-IN")}</span>
                  </div>
                )}
                <div className="mt-1 flex items-center justify-between text-base">
                  <span className="font-semibold text-slate-800">SchoolLane Price</span>
                  <span className="font-heading font-bold text-brand-navy" data-testid="kit-price">₹ {Number(kit.total_price || 0).toLocaleString("en-IN")}</span>
                </div>
                {mrpTotal > (kit.total_price || 0) && (
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-emerald-700 font-semibold">You save</span>
                    <span className="text-emerald-700 font-bold">₹ {Number(mrpTotal - (kit.total_price || 0)).toLocaleString("en-IN")}</span>
                  </div>
                )}
                <button
                  data-testid="btn-add-complete-kit"
                  onClick={addKitToCart}
                  disabled={alreadyInCart}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:bg-slate-300"
                >
                  <ShoppingCart size={16} />
                  {alreadyInCart ? "Kit already in cart" : "Add Complete Book Kit to Cart"}
                </button>
                <div className="text-[11px] text-slate-500 text-center mt-2">
                  The kit ships as one grouped item — books cannot be purchased individually.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Field({ label, required, disabled, children }) {
  return (
    <div className={disabled ? "opacity-60" : ""}>
      <div className="text-xs font-semibold text-slate-600 mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </div>
      {children}
    </div>
  );
}

function Select({ value, onChange, children, placeholder, disabled, ...rest }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm bg-white focus:outline-none focus:border-brand-blue disabled:bg-slate-50"
      {...rest}
    >
      <option value="">{placeholder}</option>
      {children}
    </select>
  );
}
